<body>
  <div class="flex flex-col min-h-screen bg-gray-50">
    <app-header></app-header>

    <main class="flex-grow flex items-center justify-center py-12 px-4">
      <div class="max-w-3xl w-full bg-white p-6 rounded-2xl shadow-lg">
        <h1
          class="text-[#134e4a] text-3xl font-bold mb-2 justify-center text-center"
        >
          Getting to Know Your Organization
        </h1>
        <p class="mb-6 text-gray-600 justify-center text-center">
          To get started, please complete the registration form below. This will
          allow us to set up your access to the resources and tools you need to
          manage your programs effectively.
        </p>

        <form [formGroup]="onboardingForm" (ngSubmit)="onSubmit()">
          <!-- Organization Information -->
          <section class="mb-6">
            <h2 class="text-lg font-semibold mb-4">Organization Information</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <mat-form-field appearance="outline">
                <mat-label>Organization Name</mat-label>
                <input matInput formControlName="organizationName" />
                <mat-error
                  *ngIf="
                    f.organizationName.invalid && f.organizationName.touched
                  "
                >
                  Organization Name is required.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Organization Type</mat-label>
                <mat-select formControlName="organizationType">
                  <mat-option value="">Select an option</mat-option>
                  <mat-option value="nonprofit">Non-Profit</mat-option>
                  <mat-option value="school">School</mat-option>
                  <mat-option value="business">Business</mat-option>
                  <mat-option value="government">Government</mat-option>
                  <mat-option value="fitness">Fitness</mat-option>
                  <mat-option value="other">Other</mat-option>
                </mat-select>
                <mat-error
                  *ngIf="
                    f.organizationType.invalid && f.organizationType.touched
                  "
                >
                  Organization Type is required.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="md:col-span-2">
                <mat-label>Website (Optional)</mat-label>
                <input matInput type="url" formControlName="website" />
                <mat-error *ngIf="f.website.invalid && f.website.touched">
                  Enter a valid website URL.
                </mat-error>
              </mat-form-field>
            </div>
          </section>

          <!-- Primary Admin Info -->
          <section class="mb-6">
            <h2 class="text-lg font-semibold mb-4">
              Primary Admin Information
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <mat-form-field appearance="outline">
                <mat-label>Full Name</mat-label>
                <input
                  matInput
                  formControlName="fullName"
                  (keydown)="onSignatureInput($event)"
                />
                <mat-error *ngIf="f.fullName.invalid && f.fullName.touched">
                  Full Name is required and must contain only letters.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Email Address</mat-label>
                <input matInput type="email" formControlName="email" />
                <mat-error
                  *ngIf="f.email.hasError('required') && f.email.touched"
                >
                  Email is required.
                </mat-error>
                <mat-error *ngIf="f.email.hasError('email') && f.email.touched">
                  Enter a valid email address.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Phone Number</mat-label>
                <input
                  matInput
                  type="tel"
                  formControlName="phone"
                  (keydown)="onPhoneInput($event, f.phone.value)"
                />
                <mat-error
                  *ngIf="f.phone.hasError('required') && f.phone.touched"
                >
                  Phone number is required.
                </mat-error>
                <mat-error
                  *ngIf="f.phone.hasError('pattern') && f.phone.touched"
                >
                  Enter a valid phone number (e.g., +1234567890).
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Role/Title</mat-label>
                <mat-select formControlName="role">
                  <mat-option value="">Select a role</mat-option>
                  <mat-option value="admin">Administrator</mat-option>
                </mat-select>
                <mat-error *ngIf="f.role.invalid && f.role.touched">
                  Role/Title is required.
                </mat-error>
              </mat-form-field>
            </div>
          </section>

          <!-- Additional Info -->
          <section class="mb-6">
            <h2 class="text-lg font-semibold mb-4">Additional Information</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <mat-form-field appearance="outline">
                <mat-label>Start Date (mm/dd/yyyy)</mat-label>
                <input
                  matInput
                  [matDatepicker]="picker1"
                  formControlName="startDate"
                  [min]="minDate"
                  readonly
                />
                <mat-datepicker-toggle
                  matSuffix
                  [for]="picker1"
                ></mat-datepicker-toggle>
                <mat-datepicker #picker1></mat-datepicker>
                <mat-error
                  *ngIf="
                    f.startDate.hasError('pastDate') && f.startDate.touched
                  "
                >
                  Start date cannot be in the past.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Number of Programs You Offer</mat-label>
                <input
                  matInput
                  formControlName="programCount"
                  (keydown)="onNumberInput($event)"
                />
                <mat-error *ngIf="f.participantCount.hasError('pattern')">
                  Only digits are allowed.
                </mat-error>
                <mat-error *ngIf="f.participantCount.hasError('min')">
                  Enter number greater than 0.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Approximate Number of Participants</mat-label>
                <input
                  matInput
                  formControlName="participantCount"
                  (keydown)="onNumberInput($event)"
                />
                <mat-error *ngIf="f.participantCount.hasError('pattern')">
                  Only digits are allowed.
                </mat-error>
                <mat-error *ngIf="f.participantCount.hasError('min')">
                  Enter number greater than 0.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="md:col-span-2">
                <mat-label>How did you hear about TrailFusion?</mat-label>
                <mat-select formControlName="hearAbout">
                  <mat-option value="">Select an option</mat-option>
                  <mat-option value="referral">Referral</mat-option>
                  <mat-option value="social">Social Media</mat-option>
                  <mat-option value="event">Event/Conference</mat-option>
                  <mat-option value="search">Search Engine</mat-option>
                  <mat-option value="ads">Online Ads</mat-option>
                  <mat-option value="other">Other</mat-option>
                </mat-select>
                <mat-error *ngIf="f.hearAbout.invalid && f.hearAbout.touched">
                  Please select one option.
                </mat-error>
              </mat-form-field>
            </div>
          </section>

          <!-- Agreement & Consent -->
          <section class="mb-6">
            <h2 class="text-lg font-semibold mb-4">Agreement & Consent</h2>
            <div class="flex flex-col gap-2">
              <mat-checkbox formControlName="confirmAccuracy">
                I confirm that the above information is accurate.
              </mat-checkbox>
              <mat-checkbox formControlName="confirmEmailUse">
                I understand that my email will be used as my primary login.
              </mat-checkbox>
              <mat-checkbox formControlName="confirmPolicy">
                I agree to TrailFusion’s
                <a (click)="privacyPolicy()" class="text-green-600 underline"
                  >Privacy Policy</a
                >
              </mat-checkbox>

              <mat-error
                *ngIf="
                  (f.confirmAccuracy.invalid && f.confirmAccuracy.touched) ||
                  (f.confirmEmailUse.invalid && f.confirmEmailUse.touched) ||
                  (f.confirmPolicy.invalid && f.confirmPolicy.touched)
                "
              >
                All consents must be checked.
              </mat-error>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <mat-form-field appearance="outline">
                <mat-label>Signature</mat-label>
                <input
                  matInput
                  formControlName="signature"
                  (keydown)="onSignatureInput($event)"
                />
                <mat-error *ngIf="f.signature.invalid && f.signature.touched">
                  Signature must only contain alphabets.
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>Date</mat-label>
                <input
                  matInput
                  formControlName="date"
                  readonly
                  class="cursor-not-allowed text-gray-400"
                />
              </mat-form-field>
            </div>
          </section>

          <!-- Submit -->
          <div class="text-center mt-6">
            <button
              mat-raised-button
              color="primary"
              type="submit"
              [disabled]="onboardingForm.invalid"
              class="cursor-pointer"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </main>

    <app-footer></app-footer>
  </div>
</body>
